import { Injectable } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { collection, collectionData, doc, Firestore, orderBy, query, serverTimestamp, Transaction, where } from '@angular/fire/firestore'


import { ActionType, ActionWithId, FreshAction, OpStatus, User } from '@innersight-os/shared/firestore-interfaces'
import { Observable, of, switchMap } from 'rxjs'
import { UsersService } from '../users/users.service'
import { DocumentData } from '@firebase/firestore-types'

export interface ActionWithUser extends ActionWithId {
  user: User;
}

@Injectable({
  providedIn: 'root'
})
export class ActionsService {
  constructor(
    private auth: Auth,
    private db: Firestore,
    private users: UsersService,
  ) {}

  getOperationActions(operationId: string): Observable<ActionWithId[]> {
    return this.users.me$.pipe(
      switchMap(me => {
        if([null, undefined].includes(me)) return of([])

        let actionQuery = query<ActionWithId, DocumentData>(
          // eslint-able-next-line @typescript-eslint/no-explicit-any
          collection(this.db, 'operations', operationId, 'actions') as any,
          orderBy('createdAt', 'desc'),
        )

        const iAmSegmenterOnly = !me.roles?.admin && !me.roles?.manager && me.roles?.segmenter
        if(iAmSegmenterOnly) actionQuery = query(
          actionQuery,
          where('userId', '==', me.id)
        )

        return collectionData(actionQuery, {idField: 'id'})
      })
    )
  }

  recordAction(userId: string | undefined, operationId: string, partialAction: {type: ActionType, message?: string}, transaction: Transaction) {
    // create action
    const actionOnUserId = userId || this.auth.currentUser.uid
    const actionRef = doc(collection(this.db, `operations/${operationId}/actions`))
    const actionData: FreshAction = {
      ...this.createdFields(),
      ...partialAction,
      operationId,
      userId: actionOnUserId
    }
    transaction.set(actionRef, actionData)

    // update op if needed
    const actionToStatus: {[P in ActionType]?: OpStatus} = {
      "accepted": "withSegmenter",
      "segmentationCompleted": "withReviewer",
      "segmentationRequested": "withSegmenter",
      "published": "published",
      "publicationRevoked": "uploaded",
    }
    const newStatus = actionToStatus[partialAction.type]
    if (newStatus) {
      const opRef = doc(this.db, `operations/${operationId}`)
      transaction.update(opRef, {status: newStatus})
    }
  }

  private createdFields = () => ({
    createdAt: serverTimestamp(),
    createdBy: this.auth.currentUser.uid,
  })
}
